import { APIError } from "snudbud-backbone-sdk";

const { ForbiddenCode, NotFoundCode, UnauthorizedCode } = APIError;

/**
 * Maps API error codes to user-friendly messages
 */
const errorMessages: Record<string | number, string> = {
  // Auth errors
  [UnauthorizedCode.RefreshTokenInvalid]: "Your session has expired. Please sign in again.",
  [UnauthorizedCode.SessionInvalid]: "Your session is invalid. Please sign in again.",
  [ForbiddenCode.UserBanned]: "Your account has been banned. Please contact support.",

  // Validation errors - using generic keys since ValidationCode might not be directly accessible
  INVALID_EMAIL: "Please enter a valid email address.",
  INVALID_PHONE: "Please enter a valid phone number.",
  INVALID_PASSWORD: "Password must be at least 8 characters long.",

  // Not found errors
  [NotFoundCode.UserNotFound]: "User not found.",

  // Default messages
  DEFAULT_VALIDATION: "Please check your input and try again.",
  DEFAULT_UNAUTHORIZED: "You are not authorized to perform this action.",
  DEFAULT_NOT_FOUND: "The requested resource was not found.",
  DEFAULT_SERVER: "Something went wrong. Please try again later.",
  DEFAULT: "An unexpected error occurred. Please try again.",
};

/**
 * Returns a user-friendly error message based on the API error
 */
export const getErrorMessage = (error: any): string => {
  if (!error) return errorMessages.DEFAULT;

  // Handle API error objects with code property
  if (error && typeof error === "object" && "code" in error) {
    return errorMessages[error.code] || errorMessages.DEFAULT;
  }

  // Handle API response errors with error codes
  if (error.response?.data?.code) {
    return errorMessages[error.response.data.code] || errorMessages.DEFAULT;
  }

  // Handle validation errors with fields
  if (error.response?.data?.fields) {
    const fields = error.response.data.fields;
    const fieldErrors = Object.keys(fields).map((field) => {
      const fieldError = fields[field];
      if (typeof fieldError === "string") return fieldError;
      return `${field}: ${fieldError.message || "Invalid value"}`;
    });

    if (fieldErrors.length) return fieldErrors.join(", ");
    return errorMessages.DEFAULT_VALIDATION;
  }

  // Handle different HTTP status codes
  if (error.response?.status) {
    switch (error.response.status) {
      case 400:
        return errorMessages.DEFAULT_VALIDATION;
      case 401:
        return errorMessages.DEFAULT_UNAUTHORIZED;
      case 404:
        return errorMessages.DEFAULT_NOT_FOUND;
      case 500:
        return errorMessages.DEFAULT_SERVER;
      default:
        return errorMessages.DEFAULT;
    }
  }

  return errorMessages.DEFAULT;
};

/**
 * Logs error and returns user-friendly message
 * Use this as a fallback when no other error handling is possible
 */
const handleError = (error: any): string => {
  console.error("API Error:", error);

  // If it's a known API error, extract and return the message
  return getErrorMessage(error);
};

export default handleError;
